import { Sparkles, BookMarked } from "lucide-react";

export interface BookInsight {
  id: string;
  insight_type: string;
  perspective: string;
  why_it_matters: string;
  mastery_principle: string;
  is_primary: boolean;
  book: {
    title: string;
    author: string;
    core_thesis?: string | null;
  };
}

interface Props {
  insight: BookInsight;
}

export default function BookInsightCard({ insight }: Props) {
  const typeLabel =
    insight.insight_type === "deep_insight"
      ? "Deep Insight"
      : insight.insight_type === "applied_insight"
      ? "Applied Insight"
      : "Book Insight";

  // Magenta / soft-purple holographic treatment for book insights
  const borderColor = "border-[hsl(310_70%_68%/0.28)]";
  const bgGradient =
    "bg-gradient-to-br from-[hsl(310_55%_20%/0.5)] via-card/45 to-[hsl(290_55%_18%/0.35)]";
  const accentText = "text-[hsl(310_85%_80%)]";
  const topGlow = "bg-gradient-to-r from-transparent via-[hsl(310_90%_70%/0.6)] to-transparent";

  return (
    <section className={`relative rounded-2xl border ${borderColor} ${bgGradient} overflow-hidden shadow-[0_0_32px_-16px_hsl(310_85%_60%/0.55)]`}>
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-px ${topGlow}`} />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-[hsl(310_90%_70%/0.7)] via-[hsl(290_80%_65%/0.4)] to-transparent" />
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 pt-5 pb-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(310_60%_45%/0.22)] ring-1 ring-[hsl(310_85%_70%/0.4)]">
          <BookMarked className={`h-4 w-4 ${accentText}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-heading font-semibold uppercase tracking-[0.2em] ${accentText}`}>
            {typeLabel}
          </p>
          <p className="font-heading text-sm font-bold text-foreground truncate">
            {insight.book.title}
          </p>
          <p className="text-xs text-muted-foreground">by {insight.book.author}</p>
        </div>
      </div>

      {/* Perspective */}
      <div className="px-5 pb-4 space-y-4">
        <div className="space-y-1.5">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            Perspective
          </p>
          <p className="font-body text-sm leading-[1.75] text-foreground/85">
            {insight.perspective}
          </p>
        </div>

        {/* Why It Matters */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            Why It Matters in Real Life
          </p>
          <p className="font-body text-sm leading-[1.75] text-foreground/85">
            {insight.why_it_matters}
          </p>
        </div>

        {/* Mastery Principle — mint/cyan holo callout */}
        <div className="relative overflow-hidden rounded-xl border border-[hsl(160_80%_60%/0.35)] bg-gradient-to-br from-[hsl(160_60%_18%/0.55)] via-background/40 to-[hsl(180_60%_18%/0.4)] p-4 flex items-start gap-3 shadow-[0_0_24px_-10px_hsl(160_90%_55%/0.6)]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(160_95%_65%/0.7)] to-transparent" />
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(160_70%_45%/0.25)] ring-1 ring-[hsl(160_90%_65%/0.45)]">
            <Sparkles className="h-4 w-4 text-[hsl(160_95%_72%)]" />
          </div>
          <div>
            <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.2em] text-[hsl(160_85%_75%)] mb-1">
              Mastery Principle
            </p>
            <p className="font-heading text-sm font-semibold leading-relaxed text-foreground">
              {insight.mastery_principle}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
